import {
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { buildNodePath, buildNodePathAbove } from '@/features/drive/entities/directory/directory-context'
import { Move, Target } from 'lucide-react'
import { useNodeMoveActionSubMenu } from '@/features/drive/functionalities/actions/node/core/node-move-action/utils/use-node-move-action-sub-menu'
import { useNodeMoveActionContext } from '@/features/drive/functionalities/actions/node/core/node-move-action/node-move-action-context'
import { NodeMoveActionSubMenu } from '@/features/drive/functionalities/actions/node/core/node-move-action/node-move-action-sub-menu'
import { useMemo } from 'react'
import { cn } from '@/utils/styles/cn'

export const NodeMoveActionDropdownSubMenu = () => {
    const { path, level, mode, isEntrypoint, onSuccess, name, as, styleType, disabled } = useNodeMoveActionContext()

    const {
        getIsSelf,
        isOpen,
        directory,
        parentNode,
        parentPath,
        filteredContent,
        showMoveHereItem,
        showGoUpItem,
        onMoveHereClick,
        handleOpenChange,
        handleArrowRight,
        handleMouseEnter,
        handleMouseLeave,
    } = useNodeMoveActionSubMenu({ path, level, mode })

    const { menuItemClassName, menuContentClassName, iconClassName, separatorClassName } = useMemo(() => {
        return {
            menuItemClassName:
                styleType === 'light'
                    ? ''
                    : 'text-[#FFFFFFF2] focus:bg-[#313136]! data-highlighted:bg-[#313136]! data-[state=open]:bg-[#313136]! data-highlighted:bg-linear-to-r data-highlighted:from-[#313136] data-highlighted:to-[#35353A] data-highlighted:text-[#FFFFFFF2] text-sm relative cursor-default truncate',
            menuContentClassName:
                styleType === 'light'
                    ? ''
                    : 'min-w-[134px] max-w-[280px] rounded-[6px] border border-[#FFFFFF14] bg-[#27272A] shadow-[0px_1px_0px_0px_rgba(255,255,255,0.20)_inset,0px_1px_2px_0px_rgba(0,0,0,0.40),0px_0px_0px_1px_#18181B]',
            iconClassName: styleType === 'light' ? '' : 'text-[#FFFFFF99]',
            separatorClassName: styleType === 'light' ? '' : 'bg-[#FFFFFF14]',
        }
    }, [styleType])

    return (
        <DropdownMenuSub open={isOpen} onOpenChange={(v) => handleOpenChange(v, isEntrypoint)}>
            <DropdownMenuSubTrigger
                onKeyDown={(e) => {
                    if (e.key === 'ArrowRight') handleArrowRight()
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={menuItemClassName}
                disabled={disabled}
            >
                {isEntrypoint && <Move className={cn('w-4 h-4', iconClassName)} />}

                {level === 0 && isEntrypoint ? 'Move' : (name ?? directory?.name)}
            </DropdownMenuSubTrigger>

            <DropdownMenuSubContent className={menuContentClassName}>
                {showMoveHereItem && (
                    <DropdownMenuItem onSelect={onMoveHereClick} className={menuItemClassName}>
                        Move here{' '}
                        <DropdownMenuShortcut>
                            <Target className={cn('h-3.5 w-3.5', iconClassName)} />
                        </DropdownMenuShortcut>
                    </DropdownMenuItem>
                )}

                {showGoUpItem && (
                    <NodeMoveActionSubMenu
                        name={`... ${parentNode?.name ?? (parentPath === '/' ? 'Drive' : 'Go up')}`}
                        level={level - 1}
                        key={'sub-menu' + path + 'up'}
                        path={buildNodePathAbove(path)}
                        onSuccess={onSuccess}
                        mode={mode}
                        as={as}
                        styleType={styleType}
                    />
                )}

                {!!filteredContent?.length && (showGoUpItem || showMoveHereItem) && (
                    <DropdownMenuSeparator className={separatorClassName} />
                )}

                {filteredContent?.map((node) => {
                    switch (node.type) {
                        case 'DIRECTORY':
                            return (
                                <NodeMoveActionSubMenu
                                    name={node.name}
                                    level={level + 1}
                                    key={'sub-menu' + path + node.slug}
                                    path={buildNodePath(path, node.slug)}
                                    onSuccess={onSuccess}
                                    mode={mode}
                                    as={as}
                                    styleType={styleType}
                                    disabled={getIsSelf(node.path)}
                                />
                            )

                        default:
                            return null
                    }
                })}

                {isEntrypoint && level === 0 && !filteredContent?.length && !showMoveHereItem && !showGoUpItem && (
                    <DropdownMenuItem disabled className={menuItemClassName}>
                        No folders available
                    </DropdownMenuItem>
                )}
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    )
}
